<template>
  <v-container>
    <h1>{{ $store.state.sensor.name }}</h1>
    <v-row>
      <v-col>
        *Widgets use placeholder data, no actual calculations are performed
      </v-col>
    </v-row>
    <v-row>
      <!-- Torque Measurement Widget -->
      <v-col cols="12" md="3">
        <v-card class="pa-4" outlined>
          <h3 class="headline">Torque Measurement</h3>
          <v-card-text>
            <v-responsive class="text-center">
              <battery-indicator 
                :percentage="batteryLevel" 
                :show-text="true"
              />
            </v-responsive>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Axial Load Widget -->
      <v-col cols="12" md="3">
        <v-card class="pa-4" outlined>
          <h3 class="headline">Axial Load</h3>
          <v-card-text>
            <v-responsive class="text-center">
              <temperature-indicator 
                :temperature="temperature" 
                :show-text="true"
              />
            </v-responsive>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Rotation Angle Widget -->
      <v-col cols="12" md="3">
        <v-card class="pa-4" outlined>
          <h3 class="headline">Rotation Angle</h3>
          <v-card-text>
            <v-responsive class="text-center">
              <div class="display-1">{{ rotationAngle }}°</div>
            </v-responsive>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Vibration Frequency Widget -->
      <v-col cols="12" md="3">
        <v-card class="pa-4" outlined>
          <h3 class="headline">Vibration Frequency</h3>
          <v-card-text>
            <v-responsive class="text-center">
              <div class="display-1">{{ vibrationFrequency }} Hz</div>
            </v-responsive>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row> </v-row>
    <!-- Second Row: List of Sensors -->
    <v-row v-if="readings">
      <v-col cols="12">
        <v-card class="pa-4 scrollable-div" outlined>
          <v-card-title>History</v-card-title>
          <v-list>
            <v-card
              class="mt-1 mb-1"
              width="100%"
              outlined
              v-for="(item, i) in angles"
              :key="i"
            >
              <v-card-title class=""
                >{{ new Date(item.date).toLocaleString() }} <v-spacer />
                <v-row>
                  <v-col>
                    <strong class="mr-2">Torque: </strong>
                    {{ item.Torque.toFixed(3) }} Nm
                  </v-col>
                  <v-col>
                    <strong class="mr-2">Axial Load: </strong>
                    {{ item.AxialLoad.toFixed(1) }} kN
                  </v-col>
                  <v-col>
                    <strong class="mr-2">Rotation Angle: </strong>
                    {{ item.RotationAngle.toFixed(0) }}°
                  </v-col>
                </v-row>
              </v-card-title>
              <hr />
              <v-container>
                <v-row>
                  <v-col>
                    <h4 class="mb-0 font-weight-light">
                      <strong>Gyroscope Values</strong><br />
                      X: {{ item.Gyroscope_X }}<br />
                      Y: {{ item.Gyroscope_Y }} <br />
                      Z: {{ item.Gyroscope_Z }}
                    </h4>
                  </v-col>
                  <v-col>
                    <h4 class="mt-0 font-weight-light">
                      <strong>Accelerometer Values</strong> <br />
                      X: {{ item.Accelerometer_X }}<br />
                      Y: {{ item.Accelerometer_Y }}<br />
                      Z:
                      {{ item.Accelerometer_Z }}
                    </h4>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { getRawData } from "../services/data";
import BatteryIndicator from '@/views/Components/BatteryIndicator.vue';
import TemperatureIndicator from '@/views/Components/TemperatureIndicator.vue';

export default {
  components: {
    BatteryIndicator,
    TemperatureIndicator,
  },
  data() {
    return {
      readings: [],
      // Placeholder data, replace with real-time data fetching
      torqueMeasurement: 55, // Nm or lb-ft
      axialLoad: 12.5, // kN or lbs
      rotationAngle: 45, // Degrees
      vibrationFrequency: 60, // Hz
      id: null,
      alertCount: 2, // Example alert count
      sensorsWithAlerts: 2, // Sensors with alerts
      totalSensors: 5,
      alerts: [
        {
          id: 1,
          sensorID: 1,
          timestamp: "2025-01-01T10:00:00Z",
        },
        {
          id: 2,
          sensorID: 1,
          timestamp: "2025-01-01T11:00:00Z",
        },
        {
          id: 3,
          sensorID: 1,
          timestamp: "2025-01-01T12:00:00Z",
        },
        {
          id: 4,
          sensorID: 1,
          timestamp: "2025-01-01T13:00:00Z",
        },
        {
          id: 5,
          sensorID: 1,
          timestamp: "2025-01-01T14:00:00Z",
        },
      ],
      sensors: [
        { id: 1, name: "Sensor 1", status: "Active" },
        { id: 2, name: "Sensor 2", status: "Alert" },
        { id: 3, name: "Sensor 3", status: "Inactive" },
        { id: 4, name: "Sensor 4", status: "Active" },
        { id: 5, name: "Sensor 5", status: "Alert" },
      ],
      angles: null,
      batteryLevel: 85,  // Example battery level
      temperature: 25,   // Example temperature
    };
  },

  computed: {
    itemId() {
      return this.$route.params.id;
    },
    location() {
      return this.$store.state.location;
    },
  },
  methods: {
    calculateRotationAngle() {
      const data = this.readings;
      const angles = [];
      let accumulatedMagnitude = 0;

      for (let i = 1; i < 99; i++) {
        const dt = data[i].Timestamp - data[i - 1].Timestamp;
        if (dt === 0) {
          continue;
        }

        const angleX = data[i].Gyroscope_X * dt;
        const angleY = data[i].Gyroscope_Y * dt;
        const angleZ = data[i].Gyroscope_Z * dt;

        const magnitude = Math.sqrt(
          angleX * angleX + angleY * angleY + angleZ * angleZ
        );

        accumulatedMagnitude += magnitude;

        angles.push({
          Timestamp: data[i].Timestamp,
          magnitude,
          accumulatedMagnitude,
          date: data[i].date,
          Rotation_X: angleX,
          Rotation_Y: angleY,
          Rotation_Z: angleZ,
          ...data[i],
        });
      }
      return angles;
    },
    calculateVibrationFrequency() {
      const vibrationData = this.readings; // Assuming sensorData is stored in Vuex

      // Assuming vibrationData is an object with keys as IDs and values as vibration readings
      const dataEntries = Object.values(vibrationData);

      if (!dataEntries || dataEntries.length === 0) {
        return 0;
      }

      // Sort the data entries by timestamp
      dataEntries.sort((a, b) => a.Timestamp - b.Timestamp);

      // Calculate the time difference between the first and last readings
      const startTime = dataEntries[0].Timestamp;
      const endTime = dataEntries[dataEntries.length - 1].Timestamp;
      const timeDifference = (endTime - startTime) / 1000; // Convert to seconds

      // Calculate the number of vibrations
      const numberOfVibrations = dataEntries.length;

      // Calculate the frequency (vibrations per second)
      const frequency = numberOfVibrations / timeDifference;

      return frequency.toFixed(2); // Return the frequency in Hz, rounded to 2 decimal places
    },
    formatDate(ts) {
      const timestamp = ts;
      const date = new Date(timestamp * 1000); // Convert to milliseconds
      return date.toLocaleString(); // Format the date as a string
    },
    fetchItemData(id) {
      // Placeholder for API call or data fetching logic
      console.log(`Fetching data for item ID: ${id}`);
      // Example of setting data dynamically:
      this.torqueMeasurement = 65;
      this.axialLoad = 10.2;
      this.rotationAngle = 30;
      this.vibrationFrequency = 55;
    },
    goToAlert(item) {
      //alert(JSON.stringify(item));
      this.$store.commit("setAlert", item);
      this.$router.push(
        "/location/" +
          this.$store.state.location.id +
          "/sensor/" +
          item.sensorID +
          "/alert/" +
          item.id
      );
    },
    async fetchSensorData() {
      const data = await getRawData();
      this.readings = data;
      this.angles = this.calculateRotationAngle();
      // .map((item) => ({
      //   ...item,
      //   ts: new Date(formatDate(item.Timestamp)),
      // }))
    },
  },
  async created() {
    this.id = this.itemId;
    await this.fetchSensorData();
  
  },
};
</script>
<style scoped>
.headline {
  text-align: center !important;
  font-size: 3rem;
  font-weight: bold;
}

.scrollable-div {
  min-height: 500px; /* Set the height you need */
  overflow-y: auto; /* Enable vertical scrolling */
  overflow-x: hidden; /* Disable horizontal scrolling */
  font-weight: 10px !important;
}
</style>
